import React, { useState, useEffect, FocusEvent, MouseEvent } from "react";
import { Row, Col, Dropdown, Button } from "react-bootstrap";
import { ActionHandlerFunction } from "@preveil-api";
import {
  Account, DriveUIActionTypes, CheckboxStates, EntryItem, getEntityIcon, CollectionEntity, NodePermissionSet, DriveEntryType, viewerUnsupported, DriveMessages,
  parseValidDate, Feature, DriveFeature, useAppDispatch, Message, DriveErrorMessages, MessageToastTypes, MessageHandlerDisplayType, useAppSelector, isUploadInProgress,
  DriveItemType
} from "src/common";
import { Checkbox, Icon } from "src/components";
import { DriveDropdown } from "./";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  current_account: Account;
  entry: EntryItem;
  collection_info?: CollectionEntity;
  is_loading: boolean;
  selected: boolean;
  dragging_items: string[];
  handleAction: ActionHandlerFunction;
}

function ListRowComponent(props: AllProps) {
  const { current_account, entry, selected, collection_info, dragging_items, is_loading, handleAction } = props;
  const dispatch = useAppDispatch();
  const upload_folder_ids = useAppSelector((state: RootState) => state.ui.upload_folder_ids);
  const [_selected, setSelected] = useState<boolean>(selected);
  const [class_name, setClassName] = useState<string>("");
  const [node_permissions, setNodePermissions] = useState<NodePermissionSet | undefined>(
    (!!collection_info && collection_info.id === entry.id) ? collection_info.permissions : undefined);
  const uploading_in_progress = isUploadInProgress(entry, upload_folder_ids);

  useEffect(() => {
    dragging_items.includes(entry.id) ? setClassName("dragging-item") : setClassName("");
  }, [dragging_items]);

  useEffect(() => {
    (!!collection_info && collection_info.id === entry.id) &&
      setNodePermissions(collection_info.permissions);
    // NOTE: Reset Node Permissions on navigation
    (!collection_info && !!node_permissions) && setNodePermissions(undefined);
  }, [collection_info]);

  useEffect(() => {
    setSelected(selected);
  }, [selected]);

  // Description: Handle the click of checkbox and changes of checkbox state 
  function handleCheckboxChange(e: FocusEvent<HTMLInputElement>) {
    const new_selected = !_selected;
    setSelected(new_selected);
    handleAction({ actionType: DriveUIActionTypes.Select, params: { selected: e.target.checked, entry } });
  }

  // Description: Check if file is valid for viewing, then build the url and navigate 
  function handleRowClick() {
    if (is_loading) {
      return false;
    }
    if (entry.type === DriveEntryType.FILE && !!viewerUnsupported(entry)) {
      dispatch(uiActions.handleSetMessage(new Message(DriveErrorMessages.file_not_supported, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
      return false;
    }
    if (!!entry.type_label && entry.type_label === DriveItemType.Video) {
      handleAction({ actionType: DriveUIActionTypes.OpenVideoIfSupported, params: entry });
      return false;
    }
    handleAction({ actionType: DriveUIActionTypes.NavigateToUrl, params: entry });
    entry.type === DriveEntryType.FILE && handleAction({ actionType: DriveUIActionTypes.DownloadAndView, params: entry });
    return false;
  }

  // Description: Tooltip for file type not supported
  function unsupportedTooltip(): string {
    return entry.type === DriveEntryType.FILE && !!viewerUnsupported(entry)
      ? DriveMessages.not_supported
      : "";
  }

  // Description: Tooltip for sync status
  function iconTooltip(): string {
    return entry.type === DriveEntryType.FILE ? "File" :
      (entry.type === DriveEntryType.LINK ? "Shared Folder" : "Folder");
  }

  // Description: uploading Tooltip
  function uploadingTooltip(): string {
    return uploading_in_progress ? DriveMessages.uploading_in_progress : "";
  }

  function handleStarClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    handleAction({ actionType: DriveUIActionTypes.SetStarState, params: { entry, star: entry.starred } });
  }

  // Description: Build the icon for the row depending on lock or uploading state
  function ListRowIcon() {
    return uploading_in_progress ?
      <span data-tooltip-id="pv-tooltip" data-tooltip-content={uploadingTooltip()}>
        <Icon className="ficon-loader" />
      </span> :
      !!entry.is_locked ?
        <Icon className="icon-lock2" /> :
        <Checkbox className="sr-only"
          onChange={(e: FocusEvent<HTMLInputElement>) => { handleCheckboxChange(e); }}
          selected={_selected ? CheckboxStates.checked : CheckboxStates.empty}
          value="all" label="Select All" disabled={upload_folder_ids.includes(entry.id)} />;
  }

  return <Row onClick={handleRowClick}
    className={`${selected ? "active " : ""}${class_name}${!!entry.is_locked || uploading_in_progress ? " locked" : ""}`}
    draggable={!Feature.hidden(DriveFeature.Move, node_permissions) && !uploading_in_progress}
    onDragStart={(e) => { !uploading_in_progress && handleAction({ actionType: DriveUIActionTypes.OnDrag, params: { e, entry } }); }}
    onDragOver={(e) => handleAction({ actionType: DriveUIActionTypes.DragOver, params: { e } })}
    onDrop={(e) => handleAction({ actionType: DriveUIActionTypes.OnDrop, params: { e } })}
    onDragLeave={(e) => handleAction({ actionType: DriveUIActionTypes.OnDragLeave, params: { e } })}
    onDragEnd={() => handleAction({ actionType: DriveUIActionTypes.OnDragEnd })}
    id={`entity-item~${!!entry.maintainer_id && entry.maintainer_id === entry.id
      ? DriveEntryType.ACL_NODE
      : entry.type
      }~${entry.id}`}>
    <Col md="1">
      {ListRowIcon()}
    </Col>
    <Col md="5" className="entry-name col">
      <span data-tooltip-id="pv-tooltip" data-tooltip-content={unsupportedTooltip()}>
        <Button variant="icon" className="drive-starred" size="sm" onClick={(e: MouseEvent<HTMLButtonElement>) => handleStarClick(e)}>
          {
            entry.starred ?
              <Icon className="pv-icon-starred" tooltip="Remove Starred Status" /> :
              <Icon className="pv-icon-unstarred" tooltip="Add Starred Status" />
          }
        </Button>
        <Icon className={entry.type_class || getEntityIcon(entry.type)} tooltip={iconTooltip()} />{entry.name}
      </span>
    </Col>
    <Col md="2" className="order-3">
      {entry.type_label}
    </Col>
    <Col md="3" className="order-4">
      {parseValidDate(entry.lastModificationDate)}
    </Col>
    <Col md="1" className="order-md-5 order-2">
      <Dropdown as="button"
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          e.preventDefault();
          document.body.click();
          handleAction({ actionType: DriveUIActionTypes.Details, params: { entry, action: DriveUIActionTypes.ToggleSidePanel } });
        }}
        className="btn btn-icon btn-sm" disabled={is_loading}>
        <Dropdown.Toggle as="span">
          <Icon className="ficon-more-vertical" />
        </Dropdown.Toggle>
        <DriveDropdown
          current_account={current_account}
          entry={entry}
          collection_info={collection_info}
          all_actions
          handleAction={handleAction} />
      </Dropdown>
    </Col>
  </Row>;
}

export default React.memo(ListRowComponent);